import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Eye, RefreshCw, ArrowRight, PauseCircle } from "lucide-react";
import { getWatchStatusForReport } from "@/lib/api";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

/**
 * WatchlistStatusBar — quiet monitoring status line on the Blueprint page.
 * States: monitored / paused / full / one_time. Renders nothing otherwise.
 */
export default function WatchlistStatusBar({ report, user }) {
  const [status, setStatus] = useState(null);
  const isOwner = user && report?.user_id && user.user_id === report.user_id;

  useEffect(() => {
    if (!isOwner) return;
    getWatchStatusForReport(report.id).then(setStatus).catch(() => {});
  }, [isOwner, report?.id]);

  if (!isOwner || !status || status.state === "none") return null;

  const scrollToRescan = () => {
    document.querySelector('[data-testid="rescan-diff-panel"]')?.scrollIntoView({ behavior: "smooth" });
  };

  const linkBtn = "inline-flex items-center gap-1.5 font-mono-plex text-[10px] uppercase tracking-[0.2em] border-2 border-[#111] px-3 py-1.5 hover:bg-[#111] hover:text-[#F4F4F0] transition-colors whitespace-nowrap";

  if (status.state === "monitored") {
    const w = status.watch;
    return (
      <section className="border-b-2 border-[#111] bg-[#EAF4EA]" data-testid="watchlist-status-monitored">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="flex-1 font-mono-plex text-[11px] uppercase tracking-[0.15em] text-[#1c4a1c] flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1.5 font-bold">
              <CheckCircle2 size={13} /> Saved to Watchlist
            </span>
            <span data-testid="watchlist-next-update">Next monthly update: {fmtDate(w.next_auto_scan_at)}</span>
            <span data-testid="watchlist-manual-status">
              {w.manual_rescan_available
                ? "Manual rescan: 1 available this billing month"
                : `Included rescan used — next on ${fmtDate(w.manual_rescan_next_at)}`}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {w.manual_rescan_available && (
              <button onClick={scrollToRescan} className={linkBtn} data-testid="watchlist-rescan-now">
                <RefreshCw size={11} /> Rescan Now
              </button>
            )}
            <Link to="/watchlist" className={linkBtn} data-testid="watchlist-view-link">
              <Eye size={11} /> View Watchlist
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (status.state === "paused") {
    return (
      <section className="border-b-2 border-[#111] bg-[#F4F4F0]" data-testid="watchlist-status-paused">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 font-mono-plex text-[11px] uppercase tracking-[0.15em] text-[#555] inline-flex items-center gap-1.5">
            <PauseCircle size={13} /> Monitoring is paused for this URL.
          </div>
          <Link to="/watchlist" className={linkBtn} data-testid="watchlist-view-link-paused">
            Manage Watchlist <ArrowRight size={11} />
          </Link>
        </div>
      </section>
    );
  }

  if (status.state === "full") {
    return (
      <section className="border-b-2 border-[#111] bg-[#FFF4E5]" data-testid="watchlist-status-full">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 text-sm text-[#333]">
            <span className="font-bold">Blueprint complete. Your Watchlist is full ({status.used} of {status.limit}).</span>{" "}
            This Blueprint is saved in Blueprint History but is not receiving monthly monitoring.
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link to="/watchlist" className={linkBtn} data-testid="watchlist-manage-link">
              Manage Watchlist
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 font-mono-plex text-[10px] uppercase tracking-[0.2em] bg-[#111] text-[#F4F4F0] px-3 py-1.5 hover:bg-[#FF331F] transition-colors whitespace-nowrap"
              data-testid="watchlist-upgrade-link"
            >
              Upgrade Plan <ArrowRight size={11} />
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (status.state === "one_time") {
    return (
      <section className="border-b-2 border-[#111] bg-[#F4F4F0]" data-testid="watchlist-status-onetime">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 text-sm text-[#333]">
            <span className="font-bold">This is a one-time Blueprint.</span>{" "}
            Upgrade to a monthly plan to automatically track this URL and see what changes.
          </div>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1.5 font-mono-plex text-[10px] uppercase tracking-[0.2em] bg-[#111] text-[#F4F4F0] px-3 py-1.5 hover:bg-[#FF331F] transition-colors whitespace-nowrap shrink-0"
            data-testid="watchlist-onetime-upgrade"
          >
            Upgrade to Monthly Monitoring <ArrowRight size={11} />
          </Link>
        </div>
      </section>
    );
  }

  return null;
}
